import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Minus, Trash2, X, MessageCircle, Download, AlertTriangle,
  Package, IndianRupee, Warehouse, LogOut, Zap, Boxes, History,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, waLink } from "@/lib/config";

const UNITS = ["Bags", "MT", "Cu.m", "Brass", "Nos", "Kg"];
const inputCls =
  "w-full bg-ink border border-white/10 focus:border-brand/60 outline-none text-sm text-gray-200 placeholder-gray-600 px-4 py-2.5 transition-colors";

function Gate() {
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const quickDemo = async () => {
    setBusy(true);
    try {
      await demoLogin();
      toast.success("Demo contractor mode active.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/login");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-ink min-h-screen pt-16 flex items-center justify-center grain">
      <div className="text-center max-w-md mx-4 bg-surface border border-white/10 p-10" data-testid="inventory-gate">
        <Warehouse className="h-12 w-12 text-brand mx-auto mb-6" />
        <h1 className="font-heading text-2xl font-extrabold uppercase text-white">Contractor Inventory Desk</h1>
        <p className="text-sm text-gray-400 mt-3 leading-relaxed">
          Login to track cement, steel and aggregate stock across your sites — with rates,
          suppliers and low-stock alerts.
        </p>
        <button
          onClick={quickDemo}
          disabled={busy}
          data-testid="inventory-btn-demo-login"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-ink font-heading font-bold uppercase tracking-wide px-6 py-3.5 transition-colors"
        >
          <Zap className="h-4 w-4" /> {busy ? "Entering…" : "1-Click Demo Contractor Mode"}
        </button>
        <Link
          to="/login"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-white/20 hover:border-brand/60 text-gray-200 hover:text-brand font-heading font-bold uppercase tracking-wide px-6 py-3.5 transition-colors text-sm"
        >
          Login / Register
        </Link>
      </div>
    </main>
  );
}

function AddMaterialModal({ sites, onClose, onCreated }) {
  const [form, setForm] = useState({
    site: sites[0] || "", newSite: "", material: "", brand: "", unit: "Bags",
    quantity: "", min_threshold: "", rate: "", supplier_name: "", supplier_contact: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        site: form.newSite.trim() || form.site,
        material: form.material,
        brand: form.brand,
        unit: form.unit,
        quantity: parseFloat(form.quantity) || 0,
        min_threshold: parseFloat(form.min_threshold) || 0,
        rate: parseFloat(form.rate) || 0,
        supplier_name: form.supplier_name,
        supplier_contact: form.supplier_contact,
      };
      const { data } = await api.post("/inventory", payload);
      onCreated(data);
      toast.success(`${data.material} added to ${data.site}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface border border-white/10 p-7 max-h-[90vh] overflow-y-auto"
        data-testid="inventory-add-material-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold uppercase text-white">Add Material</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Site</span>
              <select value={form.site} onChange={set("site")} className={inputCls} data-testid="inventory-input-site">
                {sites.length === 0 && <option value="">— create below —</option>}
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Or New Site</span>
              <input value={form.newSite} onChange={set("newSite")} placeholder="e.g. Site Gamma — Warehouse" className={inputCls} data-testid="inventory-input-new-site" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Material *</span>
              <input required value={form.material} onChange={set("material")} placeholder="e.g. Cement OPC 53" className={inputCls} data-testid="inventory-input-material" />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Brand</span>
              <input value={form.brand} onChange={set("brand")} placeholder="e.g. UltraTech" className={inputCls} data-testid="inventory-input-brand" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Unit</span>
              <select value={form.unit} onChange={set("unit")} className={inputCls} data-testid="inventory-input-unit">
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Quantity</span>
              <input type="number" min="0" step="any" value={form.quantity} onChange={set("quantity")} placeholder="0" className={inputCls} data-testid="inventory-input-quantity" />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Min. Alert</span>
              <input type="number" min="0" step="any" value={form.min_threshold} onChange={set("min_threshold")} placeholder="0" className={inputCls} data-testid="inventory-input-threshold" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Buy Rate ₹</span>
              <input type="number" min="0" step="any" value={form.rate} onChange={set("rate")} placeholder="0" className={inputCls} data-testid="inventory-input-rate" />
            </label>
            <label className="block col-span-2">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Supplier</span>
              <input value={form.supplier_name} onChange={set("supplier_name")} placeholder="Supplier name" className={inputCls} data-testid="inventory-input-supplier" />
            </label>
          </div>
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Supplier Contact</span>
            <input value={form.supplier_contact} onChange={set("supplier_contact")} placeholder="+91 …" className={inputCls} data-testid="inventory-input-supplier-contact" />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="inventory-btn-save-material"
            className="w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-ink font-heading font-bold uppercase tracking-wide px-6 py-3.5 transition-colors"
          >
            {busy ? "Saving…" : "Save Material"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function LedgerModal({ item, onClose }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    api.get(`/inventory/${item.id}/ledger`).then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, [item.id]);

  return (
    <div className="fixed inset-0 z-[60] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface border border-white/10 p-7 max-h-[85vh] overflow-y-auto"
        data-testid="inventory-ledger-modal"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl font-bold uppercase text-white">Stock Ledger</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white" aria-label="Close ledger">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="font-mono text-xs text-gray-500 mb-6">
          {item.material} · {item.site} · current {item.quantity} {item.unit}
        </p>

        {entries === null ? (
          <p className="font-mono text-sm text-gray-500 animate-pulse py-8 text-center">Loading history…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center">No movements recorded yet.</p>
        ) : (
          <div className="border border-white/10">
            {entries.map((e) => (
              <div key={e.id} data-testid="inventory-ledger-row" className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/5 last:border-0">
                <div>
                  <p className="font-mono text-xs text-gray-400">
                    {new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-600 mt-0.5">{e.note}</p>
                </div>
                <div className="text-right">
                  <p className={`font-mono font-bold text-sm ${e.delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {e.delta >= 0 ? "+" : ""}{e.delta} {item.unit}
                  </p>
                  <p className="font-mono text-[10px] text-gray-500">balance {e.quantity_after}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [activeSite, setActiveSite] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [ledgerFor, setLedgerFor] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/inventory").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [user]);

  const sites = useMemo(() => [...new Set((items || []).map((i) => i.site))], [items]);
  const visible = useMemo(
    () => (items || []).filter((i) => activeSite === "all" || i.site === activeSite),
    [items, activeSite]
  );
  const lowStock = useMemo(() => (items || []).filter((i) => i.quantity <= i.min_threshold), [items]);
  const totalValue = useMemo(() => visible.reduce((s, i) => s + i.quantity * i.rate, 0), [visible]);

  if (user === null) {
    return (
      <main className="bg-ink min-h-screen pt-16 flex items-center justify-center">
        <p className="font-mono text-sm text-gray-500 animate-pulse">Loading site desk…</p>
      </main>
    );
  }
  if (!user) return <Gate />;

  const patchItem = (id, patch) =>
    api.patch(`/inventory/${id}`, patch).then((r) => {
      setItems((prev) => prev.map((i) => (i.id === id ? r.data : i)));
    });

  const bump = (item, delta) => {
    const q = Math.max(0, item.quantity + delta);
    patchItem(item.id, { quantity: q }).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };

  const remove = (item) => {
    api.delete(`/inventory/${item.id}`).then(() => {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success(`${item.material} removed`);
    });
  };

  const exportCsv = () => {
    const rows = [
      ["Site", "Material", "Brand", "Unit", "Quantity", "Min Alert", "Rate INR", "Stock Value INR", "Supplier", "Supplier Contact"],
      ...visible.map((i) => [i.site, i.material, i.brand, i.unit, i.quantity, i.min_threshold, i.rate, Math.round(i.quantity * i.rate), i.supplier_name, i.supplier_contact]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "infra-edge-inventory.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="bg-ink min-h-screen pt-16" data-testid="inventory-dashboard-container">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-3 flex items-center gap-3">
              <span className="h-px w-10 bg-brand inline-block" /> Site Desk · {user.company || user.name}
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
              Inventory Control
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={exportCsv}
              data-testid="inventory-btn-export-csv"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-brand/60 text-gray-200 hover:text-brand font-heading font-bold text-sm uppercase tracking-wide px-4 py-2.5 transition-colors"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={() => setShowAdd(true)}
              data-testid="inventory-btn-add-material"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-ink font-heading font-bold text-sm uppercase tracking-wide px-4 py-2.5 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Material
            </button>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="inventory-btn-logout"
              className="inline-flex items-center gap-2 border border-white/10 text-gray-500 hover:text-red-400 hover:border-red-500/40 font-heading font-bold text-sm uppercase tracking-wide px-4 py-2.5 transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {lowStock.length > 0 && (
          <div
            data-testid="inventory-low-stock-alert-banner"
            className="mb-8 flex items-start gap-4 border border-red-500/40 bg-red-500/10 px-5 py-4"
          >
            <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-heading font-bold uppercase text-red-300 text-sm">
                Low stock on {lowStock.length} material{lowStock.length > 1 ? "s" : ""}
              </p>
              <p className="font-mono text-xs text-red-200/70 mt-1">
                {lowStock.map((i) => `${i.material} (${i.site}: ${i.quantity} ${i.unit})`).join(" · ")}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { icon: Boxes, label: "Materials Tracked", value: visible.length, testid: "inventory-stat-materials" },
            { icon: Warehouse, label: "Active Sites", value: sites.length, testid: "inventory-stat-sites" },
            { icon: IndianRupee, label: "Stock Value (view)", value: `₹${inr(totalValue)}`, testid: "inventory-stat-value" },
            { icon: AlertTriangle, label: "Low Stock Alerts", value: lowStock.length, testid: "inventory-stat-alerts", danger: lowStock.length > 0 },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-surface border border-white/10 p-5">
              <s.icon className={`h-5 w-5 mb-3 ${s.danger ? "text-red-400" : "text-brand"}`} />
              <p className={`font-mono text-2xl font-bold ${s.danger ? "text-red-400" : "text-white"}`}>{s.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="inventory-site-selector">
          <button
            onClick={() => setActiveSite("all")}
            className={`font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${activeSite === "all" ? "border-brand bg-brand text-ink" : "border-white/15 text-gray-400 hover:text-white"}`}
          >
            All Sites
          </button>
          {sites.map((s) => (
            <button
              key={s}
              onClick={() => setActiveSite(s)}
              className={`font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${activeSite === s ? "border-brand bg-brand text-ink" : "border-white/15 text-gray-400 hover:text-white"}`}
            >
              {s}
            </button>
          ))}
        </div>

        {items === null ? (
          <p className="font-mono text-sm text-gray-500 animate-pulse py-16 text-center">Loading materials…</p>
        ) : visible.length === 0 ? (
          <div className="border border-dashed border-white/15 py-20 text-center">
            <Package className="h-10 w-10 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-sm">No materials here yet. Add your first stock entry.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-surface font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 text-left">
                  <th className="px-4 py-3">Material / Site</th>
                  <th className="px-4 py-3">Stock</th>
                  <th className="px-4 py-3">Rate ₹</th>
                  <th className="px-4 py-3">Value ₹</th>
                  <th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((i) => {
                  const low = i.quantity <= i.min_threshold;
                  return (
                    <tr key={i.id} data-testid="inventory-table-row" className="border-t border-white/5 hover:bg-surface/60 transition-colors">
                      <td className="px-4 py-4">
                        <p className="font-heading font-bold uppercase text-white flex items-center gap-2">
                          {i.material}
                          {low && (
                            <span className="font-mono text-[9px] uppercase tracking-widest bg-red-500/15 border border-red-500/40 text-red-400 px-1.5 py-0.5">
                              Low
                            </span>
                          )}
                        </p>
                        <p className="font-mono text-[11px] text-gray-500 mt-0.5">
                          {i.brand && i.brand !== "—" ? `${i.brand} · ` : ""}{i.site}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <p className={`font-mono font-bold ${low ? "text-red-400" : "text-white"}`}>
                          {i.quantity} <span className="text-gray-500 font-normal text-xs">{i.unit}</span>
                        </p>
                        <p className="font-mono text-[10px] text-gray-600">min {i.min_threshold}</p>
                      </td>
                      <td className="px-4 py-4 font-mono text-gray-300">₹{inr(i.rate)}</td>
                      <td className="px-4 py-4 font-mono text-brand font-bold">₹{inr(i.quantity * i.rate)}</td>
                      <td className="px-4 py-4">
                        <p className="text-gray-300 text-xs">{i.supplier_name || "—"}</p>
                        <p className="font-mono text-[10px] text-gray-600">{i.supplier_contact}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => bump(i, -1)}
                            data-testid="inventory-btn-stock-decrease"
                            aria-label="Decrease stock"
                            className="p-1.5 border border-white/15 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => bump(i, 1)}
                            data-testid="inventory-btn-stock-increase"
                            aria-label="Increase stock"
                            className="p-1.5 border border-white/15 text-gray-400 hover:text-brand hover:border-brand/50 transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                          <a
                            href={waLink(`Reorder request: ${i.material}${i.brand && i.brand !== "—" ? ` (${i.brand})` : ""} — current stock ${i.quantity} ${i.unit} at ${i.site}. Please quote replenishment rate. — ${user.company || user.name}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inventory-btn-whatsapp-replenish"
                            aria-label="Reorder via WhatsApp"
                            className="p-1.5 border border-brand/40 text-brand hover:bg-brand hover:text-ink transition-colors"
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                          </a>
                          <button
                            onClick={() => setLedgerFor(i)}
                            data-testid="inventory-btn-ledger"
                            aria-label="View stock ledger"
                            className="p-1.5 border border-white/15 text-gray-400 hover:text-brand hover:border-brand/50 transition-colors"
                          >
                            <History className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => remove(i)}
                            aria-label="Delete material"
                            className="p-1.5 border border-white/15 text-gray-500 hover:text-red-400 hover:border-red-500/40 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {ledgerFor && <LedgerModal item={ledgerFor} onClose={() => setLedgerFor(null)} />}

      {showAdd && (
        <AddMaterialModal
          sites={sites}
          onClose={() => setShowAdd(false)}
          onCreated={(item) => setItems((prev) => [item, ...(prev || [])])}
        />
      )}
    </main>
  );
}
